/**
 * MomentVideoOverlay — Jun 2026 (Option A lip-sync moments)
 * Plays a lip-synced talking-head clip (with sound) on top of the AvA's
 * idle-loop tile. If the browser blocks unmuted autoplay, falls back to
 * calling onEnd so the idle loop is never covered by a frozen frame.
 * `muted` keeps the CSS-hidden breakpoint twin silent (desktop+mobile both
 * mount — only one may carry audio or the clip echoes).
 */
import React, { useEffect, useRef } from 'react';

export const MomentVideoOverlay = ({ videoUrl, onEnd, onStarted, onProgress, muted = false, testId }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => onEnd && onEnd());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  if (!videoUrl) return null;

  return (
    <div className="absolute inset-0 z-20 bg-black/20" data-testid={testId || 'moment-video-overlay'}>
      <video
        ref={videoRef}
        src={videoUrl}
        autoPlay
        playsInline
        muted={muted}
        onPlay={() => { if (!muted && onStarted) onStarted(); }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (!muted && onProgress && v.duration > 0) onProgress(v.currentTime / v.duration);
        }}
        onEnded={() => onEnd && onEnd()}
        onError={() => onEnd && onEnd()}
        className="w-full h-full object-cover"
        data-testid="moment-video-element"
      />
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onEnd && onEnd(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onEnd && onEnd(); } }}
        className="absolute top-2 right-2 px-2.5 py-1 text-[11px] font-semibold bg-black/50 text-white rounded-full backdrop-blur-sm hover:bg-black/70 transition-colors cursor-pointer select-none"
        data-testid="moment-video-skip-btn"
      >
        Skip
      </span>
    </div>
  );
};

export default MomentVideoOverlay;
